import type { IssuedLink } from '@obras/shared';
import { formatDateTime } from '@obras/shared';
import { CircleCheck, Copy, Link2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from './Button.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Link gerado, para entrega manual.
 *
 * Enquanto não há envio de e-mail (Fase 6), quem cadastra copia o link e o
 * entrega pelo canal que já usa. Deixar isso explícito é mais honesto do que
 * exibir "e-mail enviado" para um e-mail que nunca sai.
 */
export function LinkBox({ link, onClose }: { link: IssuedLink; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Área de transferência bloqueada: o link segue visível para seleção manual.
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg bg-primary-bg p-4">
        <p className="flex items-center gap-2 font-medium text-primary-strong">
          <Link2 {...ICON} size={16} aria-hidden />
          Link gerado para {link.email}
        </p>
        <p className="mt-1 text-[13px] text-primary-strong">
          O envio automático por e-mail entra na Fase 6. Copie e entregue pelo canal que você já
          usa. Este link aparece uma única vez.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          readOnly
          value={link.url}
          onFocus={(event) => event.target.select()}
          aria-label="Link gerado"
          className="h-10 w-full rounded-md bg-muted px-3 font-mono text-[13px]"
        />
        <Button
          variant={copied ? 'success' : 'secondary'}
          icon={copied ? <CircleCheck {...ICON} size={16} /> : <Copy {...ICON} size={16} />}
          onClick={() => void copy()}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>

      <p className="text-[13px] text-muted-foreground">
        Válido até {formatDateTime(link.expiresAt)}.
      </p>

      <div className="flex justify-end">
        <Button onClick={onClose}>Fechar</Button>
      </div>
    </div>
  );
}
